import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowUpRight, X, AlertCircle, Check } from "lucide-react";
import { useContent } from "../lib/useContent";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ============================================================
   ContactPanel — editorial full-viewport "brief" takeover.
   Matches V5's calm, editorial aesthetic:
   - Cabinet Grotesk display heading with mask-wipe reveal
   - Yellow `.reveal-underline` accent
   - Eyebrows + hairline-bottom inputs (no boxed fields)
   - Outlined pill service options (no icons)
   - 3-pill scope toggle
   - Submit styled identically to the hero "Start a Project" CTA
   Exposed via context — any component can call
   `useContactPanel().open()` to open it.
============================================================ */

const ContactPanelContext = createContext({
  isOpen: false,
  open: () => {},
  close: () => {},
});

export const useContactPanel = () => useContext(ContactPanelContext);

export const ContactPanelProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  // Global click interceptor — ANY anchor whose href is `#contact`
  // (or ends with `#contact`, e.g. `/path#contact`) opens the slide-in
  // Contact panel instead of navigating. Lets every CMS-edited href
  // field across the site honor the convention without per-page wiring.
  useEffect(() => {
    const handler = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target;
      if (!target || typeof target.closest !== "function") return;
      const a = target.closest('a[href]');
      if (!a) return;
      const raw = a.getAttribute("href") || "";
      const href = raw.trim().toLowerCase();
      if (href === "#contact" || href.endsWith("#contact")) {
        e.preventDefault();
        e.stopPropagation();
        open();
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [open]);

  return (
    <ContactPanelContext.Provider value={{ isOpen, open, close }}>
      {children}
      <ContactPanel isOpen={isOpen} onClose={close} />
    </ContactPanelContext.Provider>
  );
};

const SERVICE_OPTIONS = [
  { id: "shopify",   label: "Shopify Development" },
  { id: "marketing", label: "Marketing & Growth" },
  { id: "creative",  label: "Design & Creative" },
];

const SCOPE_OPTIONS = [
  { id: "full-build",   label: "Full Build" },
  { id: "partnership",  label: "Ongoing Partnership" },
  { id: "audit",        label: "Audit & Strategy" },
  { id: "conversion",   label: "Conversion Optimization" },
];

/* ----- shared atoms ----- */

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-3 text-[0.68rem] font-bold tracking-[0.26em] uppercase text-neutral-400">
    <span className="h-px w-7 bg-[#F4D64E]" />
    {children}
  </div>
);

/* Premium hairline input — subtle depth, yellow focus, red error */
const FieldInput = ({
  label, name, type = "text", required, value, onChange, placeholder, testId,
  hasError, errorHint,
}) => (
  <label className="group block">
    <span className="flex items-center justify-between text-[0.7rem] font-bold tracking-[0.22em] uppercase mb-2.5">
      <span
        className={`transition-colors ${
          hasError
            ? "text-red-400"
            : "text-neutral-400 group-focus-within:text-[#F4D64E]"
        }`}
      >
        {label}
        {required && (
          <span
            className={`ml-1 ${
              hasError ? "text-red-400" : "text-[#F4D64E]"
            }`}
          >
            *
          </span>
        )}
      </span>
      {hasError && errorHint ? (
        <span className="inline-flex items-center gap-1.5 text-red-400/90 normal-case tracking-normal font-semibold text-[0.72rem]">
          <AlertCircle className="w-3.5 h-3.5" strokeWidth={2.25} />
          {errorHint}
        </span>
      ) : null}
    </span>
    <div
      className={`relative rounded-md transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        hasError
          ? "bg-red-500/[0.04] ring-1 ring-red-500/25"
          : "bg-white/[0.025] hover:bg-white/[0.04] group-focus-within:bg-white/[0.05] group-focus-within:ring-1 group-focus-within:ring-[#F4D64E]/25"
      }`}
    >
      <input
        required={required}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        aria-invalid={hasError || undefined}
        className={`w-full bg-transparent border-0 border-b-[1.5px] px-4 py-3.5 text-white text-[1.05rem] md:text-[1.15rem] placeholder:text-neutral-500 focus:outline-none transition-colors rounded-md ${
          hasError
            ? "border-red-400/70 focus:border-red-400"
            : "border-white/15 focus:border-[#F4D64E]"
        }`}
      />
    </div>
  </label>
);

const ContactPanel = ({ isOpen, onClose }) => {
  // CMS overrides for form copy + per-field required toggles. See
  // /admin/content/contact → "Contact form — copy". Empty/undefined
  // CMS values fall back to the original hardcoded strings, so the
  // panel renders byte-identical until the admin edits a field.
  const contactCms = useContent("contact");
  const f = (contactCms && contactCms.form) || {};
  // Required toggles — default behavior preserved (service/name/email/brief
  // required; scope/company/website optional). Admin can flip each.
  const req = {
    service: f.req_service === undefined ? true  : !!f.req_service,
    scope:   f.req_scope   === undefined ? false : !!f.req_scope,
    name:    f.req_name    === undefined ? true  : !!f.req_name,
    email:   f.req_email   === undefined ? true  : !!f.req_email,
    company: f.req_company === undefined ? false : !!f.req_company,
    website: f.req_website === undefined ? false : !!f.req_website,
    brief:   f.req_brief   === undefined ? true  : !!f.req_brief,
  };
  // Footer pills — `undefined` falls back to default, but an EXPLICIT
  // empty string (admin cleared the field on purpose) hides that pill
  // and its adjacent dot separator. Built as a filtered array so the
  // dividers only appear between visible items.
  const pill = (raw, fallback) => (raw === "" ? null : (raw || fallback));
  const footerPills = [
    pill(f.footer_a, "250+ stores"),
    pill(f.footer_b, "10+ years"),
    pill(f.footer_c, "Brooklyn-based"),
  ].filter(Boolean);

  // Copy strings — `||` fallback keeps hardcoded defaults safe.
  const c = {
    eyebrow: f.eyebrow || "The Brief",
    reply_note: f.reply_note || "Reply within 24h",
    headline_main: f.headline_main || "Tell us about",
    headline_italic: f.headline_italic || "your",
    headline_accent: f.headline_accent || "project",
    lead: f.lead ||
      "Walk us through what you're building. The more we know, the sharper our first response.",
    label_service: f.label_service || "What you need",
    help_service: f.help_service === undefined ? "Select all that apply." : f.help_service,
    label_scope: f.label_scope || "Scope",
    help_scope: f.help_scope === undefined ? "Choose one or more." : f.help_scope,
    label_about: f.label_about || "About you",
    label_brief: f.label_brief || "The brief",
    ph_name: f.ph_name || "Your name",
    ph_email: f.ph_email || "you@company.com",
    ph_company: f.ph_company || "Optional",
    ph_website: f.ph_website || "https://",
    ph_brief: f.ph_brief || "Walk us through it…",
    submit_label: f.submit_label || "Send the brief",
    submit_loading: f.submit_loading || "Sending…",
    required_legend: f.required_legend || "Required",
    footer_a: f.footer_a || "250+ stores",
    footer_b: f.footer_b || "10+ years",
    footer_c: f.footer_c || "Brooklyn-based",
    contact_email_label: f.contact_email_label || "hello@sundaydigital.io",
    contact_email_href: f.contact_email_href || "",
    contact_social_label: f.contact_social_label || "@sundaydigital",
    contact_social_href: f.contact_social_href || "https://instagram.com/sundaydigital",
  };

  const [services, setServices] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    website: "",
    brief: "",
  });
  const [hp, setHp] = useState(""); // honeypot
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const briefRef = useRef(null);
  const firstErrorRef = useRef(null);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Auto-grow the brief textarea
  useEffect(() => {
    const el = briefRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, 80)}px`;
  }, [form.brief]);

  // Reset all state when the panel closes so each open is a fresh form.
  useEffect(() => {
    if (isOpen) return;
    const t = setTimeout(() => {
      setTriedSubmit(false);
      setSubmitted(false);
      setSubmitError("");
      setSubmitting(false);
      setServices([]);
      setScopes([]);
      setForm({ name: "", email: "", company: "", website: "", brief: "" });
      setHp("");
    }, 350);
    return () => clearTimeout(t);
  }, [isOpen]);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const errors = {
    service: req.service && services.length === 0 ? "Pick one" : "",
    name:    req.name    && !form.name.trim() ? "Required" : "",
    email:   req.email
      ? (!form.email.trim() ? "Required" : (!emailOk ? "Invalid email" : ""))
      : (form.email.trim() && !emailOk ? "Invalid email" : ""),
    company: req.company && !form.company.trim() ? "Required" : "",
    website: req.website && !form.website.trim() ? "Required" : "",
    brief:   req.brief   && !form.brief.trim() ? "Required" : "",
    scope:   req.scope   && scopes.length === 0 ? "Pick one" : "",
  };
  const errorCount = Object.values(errors).filter(Boolean).length;
  const canSubmit = errorCount === 0;

  // Which inputs should visibly show error state (only after a submit attempt).
  const show = (field) => triedSubmit && !!errors[field];

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!canSubmit) {
      setTriedSubmit(true);
      requestAnimationFrame(() => {
        if (firstErrorRef.current) {
          firstErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || undefined,
          website: form.website.trim() || undefined,
          brief: form.brief.trim(),
          services,
          scopes,
          // Legacy single-value fields for backward-compat with the existing
          // email template / `contact_submissions` schema. We send the FIRST
          // selection (or null) so older readers keep working.
          service: services[0] || null,
          scope: scopes[0] || null,
          hp,
        }),
      });
      let data = null;
      try { data = await res.json(); } catch (_e) { /* non-JSON */ }
      if (!res.ok) {
        const msg =
          (data && (data.detail || data.message)) ||
          "We couldn't send your brief. Please try again in a moment.";
        throw new Error(typeof msg === "string" ? msg : "Submission failed.");
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle reveal classes on open/close so the page's signature
  // mask-wipe + underline reveal grammar plays each time.
  const revealClass = isOpen ? "is-revealed" : "";

  if (!isOpen && typeof window !== "undefined") {
    // Keep the node mounted for transitions but skip scroll cost when closed.
  }

  return (
    <div
      data-variant="v5"
      data-testid="contact-panel"
      aria-hidden={!isOpen}
      className={`fixed inset-0 z-[80] bg-[#0B0F14] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        isOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      }`}
      style={{ transform: isOpen ? "scale(1)" : "scale(1.02)" }}
    >
      {/* Subtle ambient glow — same recipe as the page sections */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 520px at 80% 10%, rgba(244,214,78,0.06), transparent 60%), radial-gradient(700px 460px at 5% 95%, rgba(244,214,78,0.035), transparent 60%)",
        }}
      />

      {/* TOP BAR */}
      <div className="relative z-10 max-w-[90rem] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          type="button"
          onClick={onClose}
          data-testid="contact-back-btn"
          className="group inline-flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400 hover:text-[#F4D64E] transition-colors"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" strokeWidth={2} />
          Back to site
        </button>
        <button
          type="button"
          onClick={onClose}
          data-testid="close-panel-btn"
          aria-label="Close"
          className="inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-md border border-white/10 bg-white/[0.02] text-white hover:border-[#F4D64E]/50 hover:text-[#F4D64E] transition-colors"
        >
          <X className="w-5 h-5" strokeWidth={2} />
        </button>
      </div>

      {/* CONTENT */}
      <div className="relative z-10 h-[calc(100%-5rem)] overflow-y-auto">
        <div className="max-w-[90rem] mx-auto px-6 md:px-12 pt-8 md:pt-16 pb-16">
          {submitted ? <ContactSuccess onClose={onClose} revealClass={revealClass} /> : (
          <>
          {/* Eyebrow + index */}
          <div className="flex items-center justify-between mb-10 md:mb-14 flex-wrap gap-4">
            <span data-reveal="eyebrow" className={revealClass}>
              <Eyebrow>{c.eyebrow}</Eyebrow>
            </span>
            <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 tabular-nums">
              {c.reply_note}
            </span>
          </div>

          {/* HEADING — mask-wipe with yellow underline draw */}
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className={`font-display font-black tracking-[-0.035em] leading-[0.98] text-white max-w-[18ch] ${revealClass}`}
          >
            <span>
              {c.headline_main}{" "}
              <span className="relative inline-block">
                <span className="italic font-normal text-neutral-400">{c.headline_italic}</span>{" "}
                <span className="relative inline-block">
                  {c.headline_accent}.
                  <span
                    aria-hidden
                    className="reveal-underline absolute -bottom-2 left-0 right-0 h-[4px] bg-[#F4D64E]/80 rounded-full"
                  />
                </span>
              </span>
            </span>
          </h2>
          <style>{`
            [data-testid="contact-panel"] h2 { font-size: clamp(2.75rem, 8vw, 7rem); }
          `}</style>

          {/* Lead line */}
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className={`mt-8 md:mt-10 max-w-[58ch] text-[1.05rem] md:text-[1.2rem] text-neutral-400 leading-[1.6] ${revealClass}`}
          >
            <span>
              {c.lead}
            </span>
          </p>

          {/* FORM */}
          <form onSubmit={onSubmit} noValidate className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-14">
            {/* Honeypot — hidden from real users; bots that auto-fill all
                fields will trip it and get a silent no-op response. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
              <label>
                Leave this field empty
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                  name="hp"
                />
              </label>
            </div>
            {/* SERVICE OPTIONS — multi-select */}
            <div className="lg:col-span-12" ref={show("service") ? firstErrorRef : null}>
              <div className="mb-3 flex items-center justify-between gap-4 flex-wrap">
                <Eyebrow>{c.label_service}</Eyebrow>
                {show("service") ? (
                  <span className="inline-flex items-center gap-1.5 text-[0.72rem] font-semibold text-red-400/90">
                    <AlertCircle className="w-3.5 h-3.5" strokeWidth={2.25} />
                    Pick at least one to continue
                  </span>
                ) : null}
              </div>
              {c.help_service ? (
                <p className="mb-6 text-[0.82rem] text-neutral-500 leading-[1.55]">
                  {c.help_service}
                </p>
              ) : null}
              <div className={`flex flex-wrap gap-3 ${show("service") ? "p-3 -m-3 rounded-lg bg-red-500/[0.04] ring-1 ring-red-500/20" : ""}`}>
                {SERVICE_OPTIONS.map((opt) => {
                  const active = services.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() =>
                        setServices((prev) =>
                          prev.includes(opt.id)
                            ? prev.filter((s) => s !== opt.id)
                            : [...prev, opt.id]
                        )
                      }
                      aria-pressed={active}
                      data-testid={`service-option-${opt.id}`}
                      className={`group relative inline-flex items-center gap-3 rounded-full px-6 md:px-7 py-3.5 md:py-4 text-[0.78rem] md:text-[0.82rem] font-bold tracking-[0.18em] uppercase border transition-all duration-300 ${
                        active
                          ? "bg-[#F4D64E] text-neutral-950 border-[#F4D64E]"
                          : "text-white border-white/15 hover:border-[#F4D64E]/60 hover:text-[#F4D64E]"
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`w-2 h-2 rounded-full transition-colors ${
                          active ? "bg-neutral-950" : "bg-[#F4D64E]/60 group-hover:bg-[#F4D64E]"
                        }`}
                      />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SCOPE — multi-select */}
            <div className="lg:col-span-12" ref={show("scope") ? firstErrorRef : null}>
              <div className="mb-3 flex items-center justify-between gap-4 flex-wrap">
                <Eyebrow>{c.label_scope}</Eyebrow>
                {show("scope") ? (
                  <span className="inline-flex items-center gap-1.5 text-[0.72rem] font-semibold text-red-400/90">
                    <AlertCircle className="w-3.5 h-3.5" strokeWidth={2.25} />
                    Pick at least one to continue
                  </span>
                ) : null}
              </div>
              {c.help_scope ? (
                <p className="mb-6 text-[0.82rem] text-neutral-500 leading-[1.55]">
                  {c.help_scope}
                </p>
              ) : null}
              <div className={`flex flex-wrap gap-3 ${show("scope") ? "p-3 -m-3 rounded-lg bg-red-500/[0.04] ring-1 ring-red-500/20" : ""}`}>
                {SCOPE_OPTIONS.map((opt) => {
                  const active = scopes.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() =>
                        setScopes((prev) =>
                          prev.includes(opt.id)
                            ? prev.filter((s) => s !== opt.id)
                            : [...prev, opt.id]
                        )
                      }
                      aria-pressed={active}
                      data-testid={`scope-${opt.id}`}
                      className={`inline-flex items-center rounded-full px-6 md:px-7 py-3.5 md:py-4 text-[0.78rem] md:text-[0.82rem] font-bold tracking-[0.18em] uppercase border transition-all duration-300 ${
                        active
                          ? "bg-white/[0.05] text-[#F4D64E] border-[#F4D64E]"
                          : "text-neutral-400 border-white/10 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ABOUT YOU */}
            <div className="lg:col-span-12">
              <div className="mb-8">
                <Eyebrow>{c.label_about}</Eyebrow>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                <FieldInput
                  label="Name"
                  name="name"
                  required={req.name}
                  value={form.name}
                  onChange={onChange}
                  placeholder={c.ph_name}
                  testId="input-name"
                  hasError={show("name")}
                  errorHint={errors.name}
                />
                <FieldInput
                  label="Email"
                  name="email"
                  type="email"
                  required={req.email}
                  value={form.email}
                  onChange={onChange}
                  placeholder={c.ph_email}
                  testId="input-email"
                  hasError={show("email")}
                  errorHint={errors.email}
                />
                <FieldInput
                  label="Company"
                  name="company"
                  required={req.company}
                  value={form.company}
                  onChange={onChange}
                  placeholder={c.ph_company}
                  testId="input-company"
                  hasError={show("company")}
                  errorHint={errors.company}
                />
                <FieldInput
                  label="Website"
                  name="website"
                  type="url"
                  required={req.website}
                  value={form.website}
                  onChange={onChange}
                  placeholder={c.ph_website}
                  testId="input-website"
                  hasError={show("website")}
                  errorHint={errors.website}
                />
              </div>
            </div>

            {/* THE BRIEF */}
            <div className="lg:col-span-12">
              <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
                <Eyebrow>{c.label_brief}</Eyebrow>
                {show("brief") ? (
                  <span className="inline-flex items-center gap-1.5 text-[0.72rem] font-semibold text-red-400/90">
                    <AlertCircle className="w-3.5 h-3.5" strokeWidth={2.25} />
                    Required
                  </span>
                ) : null}
              </div>
              <div
                className={`relative rounded-md transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  show("brief")
                    ? "bg-red-500/[0.04] ring-1 ring-red-500/25"
                    : "bg-white/[0.025] focus-within:bg-white/[0.05] focus-within:ring-1 focus-within:ring-[#F4D64E]/25"
                }`}
              >
                <textarea
                  ref={briefRef}
                  required={req.brief}
                  name="brief"
                  rows={3}
                  value={form.brief}
                  onChange={onChange}
                  placeholder={c.ph_brief}
                  data-testid="input-description"
                  aria-invalid={show("brief") || undefined}
                  className={`w-full bg-transparent border-0 border-b-[1.5px] px-4 py-4 text-white text-[1.15rem] md:text-[1.35rem] leading-[1.55] placeholder:text-neutral-500 focus:outline-none transition-colors resize-none rounded-md ${
                    show("brief")
                      ? "border-red-400/70 focus:border-red-400"
                      : "border-white/15 focus:border-[#F4D64E]"
                  }`}
                />
              </div>
            </div>

            {/* SUBMIT */}
            <div className="lg:col-span-12 pt-2 space-y-6">
              {/* Missing-fields banner — only after a submit attempt */}
              {triedSubmit && !canSubmit ? (
                <div
                  role="alert"
                  data-testid="form-error-banner"
                  className="flex items-start gap-3 rounded-md border border-red-500/25 bg-red-500/[0.06] px-5 py-4 text-[0.92rem] text-red-200 leading-[1.5]"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" strokeWidth={2.25} />
                  <div>
                    <div className="font-bold tracking-[0.01em] text-white">
                      Almost there.
                    </div>
                    <div className="mt-0.5 text-neutral-300">
                      Please complete the {errorCount} required field{errorCount === 1 ? "" : "s"} above to send your brief.
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Submission error from server */}
              {submitError ? (
                <div
                  role="alert"
                  data-testid="form-submit-error"
                  className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/[0.07] px-5 py-4 text-[0.92rem] text-red-200 leading-[1.5]"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" strokeWidth={2.25} />
                  <div>
                    <div className="font-bold tracking-[0.01em] text-white">
                      Couldn&apos;t send right now.
                    </div>
                    <div className="mt-0.5 text-neutral-300">{submitError}</div>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-6">
                <p className="text-[0.72rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                  <span className="text-[#F4D64E]">*</span> {c.required_legend}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-form-btn"
                  className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-8 py-5 rounded-md transition-all duration-300 shadow-[0_12px_40px_-12px_rgba(244,214,78,0.5)] hover:shadow-[0_16px_50px_-12px_rgba(244,214,78,0.65)] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#F4D64E] disabled:hover:translate-y-0"
                >
                  {submitting ? c.submit_loading : c.submit_label}
                  {!submitting && (
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  )}
                </button>
              </div>
            </div>
          </form>

          {/* BOTTOM STRIP — quiet social proof + contact */}
          <div className="mt-20 md:mt-28 pt-8 border-t border-white/[0.06] flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {footerPills.map((label, i) => (
                <span key={i} className="inline-flex items-center gap-x-6">
                  {i > 0 ? <span className="w-1 h-1 rounded-full bg-neutral-700" /> : null}
                  <span>{label}</span>
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-neutral-400">
              <a
                href={c.contact_email_href || `mailto:${c.contact_email_label || "hello@sundaydigital.io"}`}
                className="hover:text-[#F4D64E] transition-colors normal-case tracking-normal text-[0.85rem] font-normal"
              >
                {c.contact_email_label || "hello@sundaydigital.io"}
              </a>
              <span className="w-1 h-1 rounded-full bg-neutral-700" />
              <a
                href={c.contact_social_href || "https://instagram.com/sundaydigital"}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#F4D64E] transition-colors normal-case tracking-normal text-[0.85rem] font-normal"
              >
                {c.contact_social_label || "@sundaydigital"}
              </a>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ----- Success view shown after a successful submission ----- */
const ContactSuccess = ({ onClose, revealClass }) => (
  <div
    data-testid="contact-success"
    className="min-h-[60vh] flex flex-col items-start justify-center max-w-[58ch]"
  >
    <span data-reveal="eyebrow" className={revealClass}>
      <Eyebrow>Brief received</Eyebrow>
    </span>

    <div
      aria-hidden
      className="mt-10 inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#F4D64E]/10 ring-1 ring-[#F4D64E]/40"
    >
      <Check className="w-10 h-10 text-[#F4D64E]" strokeWidth={2.5} />
    </div>

    <h2
      data-reveal="mask-block"
      style={{ "--i": 1 }}
      className={`mt-10 font-display font-black tracking-[-0.035em] leading-[0.98] text-white max-w-[18ch] ${revealClass}`}
    >
      <span>
        Your request has been{" "}
        <span className="relative inline-block">
          successfully submitted.
          <span
            aria-hidden
            className="reveal-underline absolute -bottom-2 left-0 right-0 h-[4px] bg-[#F4D64E]/80 rounded-full"
          />
        </span>
      </span>
    </h2>
    <style>{`
      [data-testid="contact-panel"] [data-testid="contact-success"] h2 {
        font-size: clamp(2.25rem, 6vw, 4.5rem);
      }
    `}</style>

    <p
      data-reveal="mask-block"
      style={{ "--i": 2 }}
      className={`mt-8 text-[1.05rem] md:text-[1.2rem] text-neutral-400 leading-[1.6] ${revealClass}`}
    >
      <span>Our team will get back to you shortly.</span>
    </p>

    <button
      type="button"
      onClick={onClose}
      data-testid="contact-success-close"
      className="mt-12 group inline-flex items-center gap-3 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-all duration-300 shadow-[0_12px_40px_-12px_rgba(244,214,78,0.5)] hover:shadow-[0_16px_50px_-12px_rgba(244,214,78,0.65)] hover:-translate-y-0.5"
    >
      Back to site
      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </button>
  </div>
);

export default ContactPanel;
